import { useState } from 'react'
import { forwardRef } from 'react'
import { useForm } from 'react-hook-form'
import { useSchedulersSchedulerFeedback } from '@cocoonspace/api'
import { Badge } from '@cocoonspace/ui/components/badge'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { Form } from '@cocoonspace/ui/components/form'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import { Textarea } from '@cocoonspace/ui/components/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { ArrowUpRightIcon } from 'lucide-react'
import { toast } from 'sonner'
import * as z from 'zod/v4'

import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { useUserContext } from '~/providers/user-provider'

type FeedbackFormData = z.infer<typeof feedbackSchema>

const feedbackSchema = z.object({
	message: z.string().min(1),
})

const BetaBadge = forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
	const { t } = useLingui()

	return (
		<Badge
			size='sm'
			className='transform-y-[-20px] hover:bg-primary hover:text-primary-foreground group absolute ml-2 cursor-pointer px-2 pl-0 transition-all duration-300'
			ref={ref}
			{...props}
		>
			<Badge
				size='sm'
				variant='error'
				className='group-hover:bg-card group-hover:text-card-foreground px-2 transition-all duration-300'
			>
				Beta
			</Badge>

			<span className='ml-2 flex items-center gap-1 text-xs font-normal'>
				{t`Faites nous un retour !`}
				<ArrowUpRightIcon className='!size-3' />
			</span>
		</Badge>
	)
})

BetaBadge.displayName = 'BetaBadge'

const FeedbackDialogContent = ({ onSuccess }: { onSuccess?: VoidFunction }) => {
	const { t } = useLingui()
	const { userQuery } = useUserContext()
	const httpErrorMsg = useHttpErrorMsg()

	const { mutateAsync: sendFeedback, isPending } = useSchedulersSchedulerFeedback()

	const form = useForm<FeedbackFormData>({
		resolver: zodResolver(feedbackSchema),
		values: {
			message: '',
		},
	})

	const onSubmit = (data: FeedbackFormData) => {
		const query = sendFeedback({
			data: {
				email: userQuery.data?.email ?? '',
				message: data.message,
			},
		}).then(() => {
			onSuccess?.()
		})

		toast.promise(query, {
			loading: t`Envoi en cours...`,
			success: t`Message envoyé avec succès.`,
			error: (err) => {
				const key = `api.${err.response?.data?.errors[0].code}`
				return httpErrorMsg[key]?.() || t`Une erreur est survenue`
			},
		})
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<div className='flex flex-col gap-4'>
					<FormControlledInput
						name='message'
						render={({ field }) => (
							<Textarea
								{...field}
								placeholder={t`Faites nous un retour sur cette fonctionnalité !`}
							/>
						)}
					/>
				</div>

				<DialogFooter>
					<DialogClose asChild>
						<Button
							variant='ghost'
							size='sm'
						>
							{t`Fermer`}
						</Button>
					</DialogClose>

					<Button
						type='submit'
						size='sm'
						disabled={isPending}
					>
						{t`Envoyer`}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	)
}

export const BetaBadgeDialog = () => {
	const { t } = useLingui()
	const [isOpen, setIsOpen] = useState(false)

	return (
		<Dialog
			open={isOpen}
			onOpenChange={setIsOpen}
		>
			<DialogTrigger asChild>
				<BetaBadge />
			</DialogTrigger>

			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t`Faites nous un retour !`}</DialogTitle>
				</DialogHeader>

				<DialogDescription className='sr-only'>{t`Faites nous un retour !`}</DialogDescription>
				<FeedbackDialogContent onSuccess={() => setIsOpen(false)} />
			</DialogContent>
		</Dialog>
	)
}
